import type { TFunction } from 'i18next';
import type { AppSettings, QuoteSections } from '../types';
import { vatRates } from './index';

/**
 * Build the default (all-disabled) quote section set.
 * Enabling a section in the Calculator prefills these defaults, which the
 * user can then edit per quote (spec decision 2: defaults + per-quote edit).
 * Texts come from i18n under quoteSections.defaults.* so the print language
 * and UI language stay consistent.
 */
export function makeDefaultSections(settings: AppSettings, t: TFunction): QuoteSections {
  const vatPercent = ((vatRates[settings.companyCountry] ?? 0.255) * 100)
    .toFixed(1)
    .replace('.', ',')
    .replace(',0', '');

  // "Aku Sveins, Service" -> title "Service"
  const signatureTitle = (() => {
    const sig = settings.userSignature || '';
    const name = settings.userFullName || '';
    if (sig.startsWith(name) && sig.length > name.length) {
      return sig.slice(name.length).replace(/^[,\s]+/, '').trim();
    }
    return '';
  })();

  return {
    technical: { enabled: false, rows: [] },
    scope: {
      enabled: false,
      rows: [
        {
          party: t('quoteSections.defaults.scope.haltonParty'),
          content: t('quoteSections.defaults.scope.haltonContent'),
        },
        {
          party: t('quoteSections.defaults.scope.customerParty'),
          content: t('quoteSections.defaults.scope.customerContent'),
        },
      ],
    },
    terms: {
      enabled: false,
      rows: [
        {
          param: t('quoteSections.defaults.terms.validity'),
          value: t('quoteSections.defaults.terms.validityValue'),
        },
        {
          param: t('quoteSections.defaults.terms.deliveryTime'),
          value: t('quoteSections.defaults.terms.deliveryTimeValue'),
        },
        {
          param: t('quoteSections.defaults.terms.installationSchedule'),
          value: t('quoteSections.defaults.terms.installationScheduleValue'),
        },
        {
          param: t('quoteSections.defaults.terms.warranty'),
          value: t('quoteSections.defaults.terms.warrantyValue'),
        },
        {
          param: t('quoteSections.defaults.terms.additionalWork'),
          value: t('quoteSections.defaults.terms.additionalWorkValue'),
        },
        {
          param: t('quoteSections.defaults.terms.scopeLimits'),
          value: t('quoteSections.defaults.terms.scopeLimitsValue'),
        },
        {
          param: t('quoteSections.defaults.terms.payment'),
          value: t('quoteSections.defaults.terms.paymentValue', {
            days: settings.paymentTermsDays,
          }),
        },
        {
          param: t('quoteSections.defaults.terms.lateInterest'),
          value: t('quoteSections.defaults.terms.lateInterestValue'),
        },
        {
          param: t('quoteSections.defaults.terms.vat'),
          value: t('quoteSections.defaults.terms.vatValue', { percent: vatPercent }),
        },
        {
          param: t('quoteSections.defaults.terms.contractTerms'),
          value: t('quoteSections.defaults.terms.contractTermsValue'),
        },
      ],
    },
    attachments: { enabled: false, items: [] },
    signature: {
      enabled: false,
      name: settings.userFullName || '',
      title: signatureTitle,
      email: settings.userEmail || '',
      phone: settings.userPhone || '',
    },
  };
}
